import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import type {
  ActionView,
  CelebrationCue,
  EventId,
  HabitCategory,
  IsoDate,
  ObjectiveId,
  ObjectiveSummaryView,
  ObjectiveView,
  RewardConfig,
  WeekStart,
} from '@contracts';
import { DEFAULT_REWARD_CONFIG } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { RailProgressLine } from '@ui/shared/primitives/RailProgressLine';
import { OfflineChip } from '@ui/shared/primitives/OfflineChip';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { ChevronLeft, ChevronRight, ArrowRight, Check, Pencil } from '@ui/shared/icons';
import { formatShortDate, localIsoDate } from '@ui/shared/format/date';
import { resolveLevelIdentity } from '@ui/shared/rewards/levelProgress';
import { XPToast } from '@ui/shared/celebrations/XPToast';
import { LevelUpMoment } from '@ui/shared/celebrations/LevelUpMoment';
import { BadgeEarnSheet } from '@ui/shared/celebrations/BadgeEarnSheet';
import { useCelebrationQueue } from '@ui/shared/celebrations/useCelebrationQueue';
import { LinkedHabitsSection } from './LinkedHabitsSection';

/**
 * Objective Detail screen (design/screens/objective-detail.md). "Rail"
 * layout exactly: header switcher, title block, single continuous progress
 * line, REALISM PROJECTION inline-card adjustment offer, dated module list.
 * Screen-wide accent is always hue.primary/hue.primary.vivid — never a
 * category accent (the v1.1 bug fix this spec calls out explicitly).
 *
 * All spacing/sizing in this screen resolves through design tokens (AC-16):
 * the module-dot diameter uses icon.md (an exact 20px match this file
 * previously missed while only checking the `space`/`radius` scales), the
 * subtitle line's font size uses type.caption (also the "Minimum text size
 * anywhere: 13px" floor — the previous 12.5px literal quietly violated it),
 * and the remaining micro-offsets (formerly 3px/5px/11px literals) are
 * rounded to their nearest space.* token rather than left unmapped — a
 * deliberate small visual rounding versus the reference file's exact pixel
 * values, in favor of having zero values outside the token layer.
 */
export function ObjectiveDetailScreen({
  objectiveId,
  onNavigate,
  onEdit,
  onBack,
}: {
  readonly objectiveId: string;
  readonly onNavigate: (nextObjectiveId: string) => void;
  /**
   * New feature (owner direction: close the "zero ways to edit anything
   * after first-run" gap). Optional so existing callers/tests that don't
   * care about editing (e.g. a future embedded/read-only view) aren't
   * forced to wire it — the header edit icon simply doesn't render without
   * it.
   */
  readonly onEdit?: () => void;
  /**
   * Bug fix (found during the previous round's own verification, spawned as
   * a follow-up and now folded back into this session): there was no way
   * back to Today from this screen at all — only the edit icon and the
   * prev/next objective switcher, neither of which returns to Today. Same
   * "Back to Today" pattern already used by NewObjectiveScreen/
   * EditObjectiveScreen (ChevronLeft + "Today", top-left, muted caption
   * text). Optional for the same reason onEdit is: existing/future callers
   * that don't need it aren't forced to wire it.
   */
  readonly onBack?: () => void;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  const [objective, setObjective] = useState<ObjectiveView | null>(null);
  const [siblings, setSiblings] = useState<readonly ObjectiveSummaryView[]>([]);
  const [offline, setOffline] = useState(false);
  const [rewardConfig, setRewardConfig] = useState<RewardConfig>(DEFAULT_REWARD_CONFIG);
  // objective-detail.md §1.5: linkedHabits (ObjectiveView) carries only
  // habitId/name/currentWeek -- category and habitKind (needed for the
  // section's icon tile and build-vs-avoid phrasing) live on HabitView, so
  // this screen joins the two by habitId rather than the domain layer
  // duplicating habit fields onto every objective read.
  const [habitsById, setHabitsById] = useState<ReadonlyMap<string, { readonly category: HabitCategory; readonly habitKind: 'build' | 'avoid' }>>(
    new Map(),
  );

  const celebration = useCelebrationQueue({
    resolveIdentity: (level) =>
      resolveLevelIdentity({ xpTotal: 0, level, xpIntoCurrentLevel: 0, xpRequiredForNextLevel: 100, streakDays: 0 }, rewardConfig)
        .identity,
  });

  const handleCue = useCallback(
    (cue: CelebrationCue, sourceLabel: string) => {
      if (cue.xpAwarded <= 0 && !cue.levelUp && cue.badgesEarned.length === 0) return;
      void api.getRewardSnapshot().then((snapshot) => {
        celebration.enqueue(cue, sourceLabel, snapshot.xpTotal);
      });
    },
    [api, celebration],
  );

  // Reset local view state whenever the objectiveId prop changes, so
  // switching objectives never leaves stale content from the previous one
  // on screen mid-fetch (QA finding: switcher must not leak state across
  // objectives).
  useEffect(() => {
    setObjective(null);
  }, [objectiveId]);

  const load = useCallback(async () => {
    const [obj, list, config, habits] = await Promise.all([
      api.getObjective(objectiveId as ObjectiveId),
      api.listObjectives(),
      api.getRewardConfig(),
      api.listHabits(),
    ]);
    setObjective(obj);
    setSiblings(list);
    setRewardConfig(config);
    setHabitsById(
      new Map(habits.map((h) => [h.habitId, { category: h.category, habitKind: h.plan.habitKind }])),
    );
  }, [api, objectiveId]);

  useEffect(() => {
    void load();
  }, [load, version]);

  useEffect(() => {
    const status = api.getSyncStatus();
    setOffline(status.state === 'offline_queued');
  }, [api, version]);

  const position = useMemo(() => {
    const index = siblings.findIndex((s) => s.objectiveId === objectiveId);
    return { index, count: siblings.length };
  }, [siblings, objectiveId]);

  const goToOffset = useCallback(
    (offset: number) => {
      if (position.index < 0) return;
      const targetIndex = position.index + offset;
      if (targetIndex < 0 || targetIndex >= siblings.length) return; // no wrap (spec SS1.1)
      const target = siblings[targetIndex];
      if (target) onNavigate(target.objectiveId);
    },
    [position, siblings, onNavigate],
  );

  if (!objective) {
    return (
      <div style={{ padding: 'var(--space-6) var(--layout-page-margin-phone) 0' }}>
        <SkeletonBlock height={20} width={80} />
        <div style={{ height: 'var(--space-4)' }} />
        <SkeletonBlock height={32} />
        <div style={{ height: 'var(--space-6)' }} />
        <SkeletonBlock height={60} />
      </div>
    );
  }

  return (
    <main aria-label={`Objective: ${objective.title}`}>
      <ObjHeaderSwitcher
        position={position}
        objectiveTitles={siblings}
        onStepBack={() => goToOffset(-1)}
        onStepForward={() => goToOffset(1)}
        onEdit={onEdit}
        onBack={onBack}
      />

      {offline && <OfflineChip />}

      <ObjTitleBlock objective={objective} />

      <RealismProjectionSection
        objective={objective}
        onApply={async () => {
          const suggestion = objective.forecast.suggestion;
          if (!suggestion) return;
          // Rewards v1 (RW-2, badges-and-celebrations.md SS3): accepting a
          // forecast suggestion earns the date-adjustment realism bonus and
          // must feel like the smart play — same celebration path as any
          // other completion, sourced from the REAL CelebrationCue the
          // command returns, never fake/local state.
          const cue = await api.acceptObjectiveDateSuggestion({
            objectiveId: objective.objectiveId,
            currentTargetDate: objective.targetDate,
            suggestedTargetDate: suggestion.suggestedTargetDate,
            requiredPaceActionsPerWeek: suggestion.requiredPaceActionsPerWeek,
            reviewWeek: currentWeekStart(),
          });
          handleCue(cue, 'Date adjusted');
        }}
        onUndo={async (adjustmentEventId) => {
          // Real domain revert (not local-state-only): emits
          // log_entry.reverted targeting the objective.target_date_adjusted
          // event, which the domain's subscribe -> reload cycle then
          // reflects in a fresh getObjective() fetch (the `load()` effect
          // above, triggered by useDomainVersion picking up the append).
          // The receipt re-derives from that real fetch, not from anything
          // remembered locally.
          await api.revertLogEntry(adjustmentEventId);
        }}
      />

      <ModuleList
        objective={objective}
        onToggleAction={async (action) => {
          if (action.completed && action.entryEventId) {
            await api.revertLogEntry(action.entryEventId);
          } else {
            const cue = await api.completeAction(
              objective.objectiveId,
              action.actionId,
              localIsoDate(new Date()) as IsoDate,
            );
            handleCue(cue, 'Action done');
          }
        }}
      />

      <LinkedHabitsSection linkedHabits={objective.linkedHabits} habitsById={habitsById} />

      {celebration.xpToast && (
        <XPToast
          amount={celebration.xpToast.amount}
          sourceLabel={celebration.xpToast.sourceLabel}
          onDismiss={celebration.dismissToast}
        />
      )}
      {celebration.levelUp && (
        <LevelUpMoment
          level={celebration.levelUp.level}
          identity={celebration.levelUp.identity}
          xpTotal={celebration.levelUp.xpTotal}
          onDismiss={celebration.dismissLevelUp}
        />
      )}
      {celebration.visibleBadgeId && (
        <ObjectiveBadgeEarnLoader badgeId={celebration.visibleBadgeId} onDismiss={celebration.dismissNextBadge} />
      )}
    </main>
  );
}

/** Mirrors TodayView's BadgeEarnSheetLoader — resolves a BadgeId to its BadgeView before rendering the earn sheet. */
function ObjectiveBadgeEarnLoader({
  badgeId,
  onDismiss,
}: {
  readonly badgeId: string;
  readonly onDismiss: () => void;
}) {
  const api = useDomainApi();
  const [badge, setBadge] = useState<Awaited<ReturnType<typeof api.listBadges>>[number] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.listBadges().then((badges) => {
      if (cancelled) return;
      setBadge(badges.find((b) => b.badgeId === badgeId) ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [api, badgeId]);

  if (!badge) return null;
  return <BadgeEarnSheet badge={badge} onDismiss={onDismiss} />;
}

/**
 * DOMAIN-API GAP (flagged in the final dev-ui report): WeekStart is a
 * branded type owned by the domain layer (ADR-0006's Mon-Sun week rule), and
 * `getCurrentReviewWeek()` exists on DomainApi but is documented as "the
 * review week the UI should present now" — a weekly-review concept, not
 * necessarily "the Monday of the calendar week containing today" needed here
 * for `ObjectiveDateSuggestionView.reviewWeek` when accepting a forecast
 * suggestion OUTSIDE the weekly review (Phase 1 has no weekly review UI).
 * This local placeholder duplicates Monday-of-week math that ADR-0006
 * assigns to the domain layer — it should be replaced by a real domain query
 * (e.g. a `getCurrentWeekStart()` addition) rather than left as
 * component-side date math.
 *
 * Bug fix (timezone): the Monday-finding math (`setDate`) is correctly
 * local-time, but formatting the result via `toISOString()` reads back UTC
 * — in any timezone behind UTC this could report the wrong Monday near a
 * day boundary. `localIsoDate` (mirrors the domain layer's own
 * `ClockPort`) fixes this the same way it fixed TodayView.tsx's
 * "today" computation.
 */
function currentWeekStart(): WeekStart {
  const now = new Date();
  const day = now.getDay();
  const diff = (day + 6) % 7;
  now.setDate(now.getDate() - diff);
  return localIsoDate(now) as WeekStart;
}

function ObjHeaderSwitcher({
  position,
  objectiveTitles,
  onStepBack,
  onStepForward,
  onEdit,
  onBack,
}: {
  readonly position: { readonly index: number; readonly count: number };
  readonly objectiveTitles: readonly ObjectiveSummaryView[];
  readonly onStepBack: () => void;
  readonly onStepForward: () => void;
  readonly onEdit?: () => void;
  readonly onBack?: () => void;
}) {
  const atStart = position.index <= 0;
  const atEnd = position.index < 0 || position.index >= position.count - 1;
  const prevTitle = position.index > 0 ? objectiveTitles[position.index - 1]?.title : undefined;
  const nextTitle =
    position.index >= 0 && position.index < position.count - 1
      ? objectiveTitles[position.index + 1]?.title
      : undefined;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-4) var(--layout-page-margin-phone) 0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to Today"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              color: 'var(--text-muted)',
              fontSize: 'var(--type-caption-size)',
              fontWeight: 600,
              minHeight: 'var(--touch-target-min)',
              marginLeft: '-8px',
            }}
          >
            <ChevronLeft size={16} aria-hidden="true" />
            Today
          </button>
        ) : (
          <span aria-hidden="true" />
        )}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            aria-label="Edit objective"
            style={{
              width: 'var(--touch-target-min)',
              height: 'var(--touch-target-min)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Pencil size={16} color="var(--text-muted)" aria-hidden="true" />
          </button>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <button
          type="button"
          onClick={onStepBack}
          disabled={atStart}
          aria-disabled={atStart}
          aria-label={prevTitle ? `Previous objective: ${prevTitle}` : 'Previous objective'}
          style={{
            width: 'var(--touch-target-min)',
            height: 'var(--touch-target-min)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: atStart ? 0.4 : 1,
          }}
        >
          <ChevronLeft size={16} color="var(--text-muted)" aria-hidden="true" />
        </button>
        <span
          className="num"
          style={{
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
            letterSpacing: '0.1em',
            color: 'var(--text-muted)',
          }}
        >
          {position.index >= 0 ? `OBJ ${position.index + 1} / ${position.count}` : ''}
        </span>
        <button
          type="button"
          onClick={onStepForward}
          disabled={atEnd}
          aria-disabled={atEnd}
          aria-label={nextTitle ? `Next objective: ${nextTitle}` : 'Next objective'}
          style={{
            width: 'var(--touch-target-min)',
            height: 'var(--touch-target-min)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: atEnd ? 0.4 : 1,
          }}
        >
          <ChevronRight size={16} color="var(--text-muted)" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function ObjTitleBlock({ objective }: { readonly objective: ObjectiveView }) {
  const hasTarget = Boolean(objective.targetDate);
  return (
    <div style={{ padding: 'var(--space-5) var(--layout-page-margin-phone) 0' }}>
      <div
        style={{
          fontSize: 'var(--type-caption-size)',
          textTransform: 'uppercase',
          letterSpacing: '0.18em',
          color: 'var(--text-muted)',
          fontWeight: 500,
        }}
      >
        Objective
      </div>
      <h1
        style={{
          fontSize: 'var(--type-display-size)',
          lineHeight: 'var(--type-display-line)',
          fontWeight: 'var(--type-display-weight)',
          color: 'var(--text-primary)',
          margin: 0,
          marginTop: 'var(--space-2)',
          letterSpacing: '-0.01em',
        }}
      >
        {objective.title}
      </h1>
      <div
        className="num"
        style={{
          display: 'flex',
          gap: 'var(--space-4)',
          marginTop: 'var(--space-3)',
          fontSize: 'var(--type-caption-size)',
          color: 'var(--text-muted)',
          fontWeight: 500,
        }}
      >
        {hasTarget && <span>Target · {formatShortDate(objective.targetDate)}</span>}
        <span style={{ color: 'var(--hue-primary)', fontWeight: 600 }}>
          {objective.progress.done} / {objective.progress.total} complete
        </span>
      </div>
      <RailProgressLine done={objective.progress.done} total={objective.progress.total} />
    </div>
  );
}

function RealismProjectionSection({
  objective,
  onApply,
  onUndo,
}: {
  readonly objective: ObjectiveView;
  readonly onApply: () => Promise<void>;
  readonly onUndo: (adjustmentEventId: EventId) => Promise<void>;
}) {
  const { forecast, progress } = objective;

  const wrapperStyle: CSSProperties = {
    margin: 'var(--space-6) var(--layout-page-margin-phone) 0',
    padding: 'var(--space-4) 0',
    borderTop: '1px solid var(--border-subtle)',
    borderBottom: '1px solid var(--border-subtle)',
  };

  // The receipt state is driven by the domain's own record of an applied
  // adjustment (forecast.appliedAdjustmentEventId), not local component
  // state — the domain event log is the single source of truth for whether
  // an adjustment is in effect (fixed after a real bug: an earlier version
  // of this component tracked "accepted" purely locally, so Undo only ever
  // cleared the button's own state while the objective.target_date_adjusted
  // event stayed in the log — the UI could show "not adjusted" while the
  // saved data still had the adjustment applied). Accepting a suggestion
  // still shows this receipt correctly even once the forecast recomputes
  // against the new (usually no-longer-slipping) target date, because this
  // check no longer depends on forecast.isSlipping at all.
  if (forecast.appliedAdjustmentEventId !== null) {
    const adjustmentEventId = forecast.appliedAdjustmentEventId;
    return (
      <div style={wrapperStyle}>
        <Label>Realism projection</Label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--type-body-size)', color: 'var(--text-primary)', marginTop: 'var(--space-3)' }}>
          <Check size={16} color="var(--hue-secondary)" aria-hidden="true" />
          Target moved to {formatShortDate(objective.targetDate)}.
          <button
            type="button"
            onClick={() => void onUndo(adjustmentEventId)}
            style={{
              color: 'var(--text-secondary)',
              textDecoration: 'underline',
              fontWeight: 400,
              padding: 'var(--space-1)',
              marginLeft: 'var(--space-1)',
            }}
          >
            Undo
          </button>
        </div>
      </div>
    );
  }

  if (progress.total > 0 && progress.done === progress.total) {
    return (
      <div style={wrapperStyle}>
        <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          All modules complete.
        </p>
      </div>
    );
  }

  if (forecast.paceActionsPerWeek === null || forecast.forecastFinishDate === null) {
    return (
      <div style={wrapperStyle}>
        <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          Forecast appears once there's a couple weeks of action history.
        </p>
      </div>
    );
  }

  if (!forecast.isSlipping) {
    // daysBetween(a, b) = a - b (see the function below). We want
    // target - finish, positive when the forecast finish lands before the
    // target date ("days early") — so target is the first argument.
    const daysEarly = daysBetween(objective.targetDate, forecast.forecastFinishDate);
    return (
      <div style={wrapperStyle}>
        <Label>Realism projection</Label>
        <Verdict>
          Pace is steady. At this rate you land <Em>{daysEarly} days early</Em> — the target could
          move in without straining the run.
        </Verdict>
      </div>
    );
  }

  const suggestion = forecast.suggestion;

  return (
    <div style={wrapperStyle}>
      <Label>Realism projection</Label>
      <Verdict>
        At {forecast.paceActionsPerWeek} actions a week you'll land past target — a gentler
        runway keeps this on track.
      </Verdict>
      {suggestion && (
        <div
          role="button"
          tabIndex={0}
          aria-label={`Pull target to ${formatShortDate(suggestion.suggestedTargetDate)}, keeps the same weekly pace`}
          onClick={() => void onApply()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              void onApply();
            }
          }}
          style={{
            marginTop: 'var(--space-4)',
            background: 'var(--surface-raised)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3) var(--space-4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            cursor: 'pointer',
          }}
        >
          <div>
            <div style={{ fontSize: 'var(--type-body-size)', fontWeight: 600, color: 'var(--text-primary)' }}>
              Move target to {formatShortDate(suggestion.suggestedTargetDate)}
            </div>
            <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-1)' }}>
              keeps this within reach
            </div>
          </div>
          <span
            style={{
              fontSize: 'var(--type-caption-size)',
              fontWeight: 600,
              color: 'var(--hue-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              flex: 'none',
            }}
          >
            Apply <ArrowRight size={15} color="var(--hue-primary-vivid)" aria-hidden="true" />
          </span>
        </div>
      )}
    </div>
  );
}

function Label({ children }: { readonly children: string }) {
  return (
    <div
      // NOTE: the design prototype's "REALISM PROJECTION" label renders at
      // 10px, but tokens.md SS2 states "Minimum text size anywhere: 13px".
      // Using type.caption (13px) here instead of the prototype's literal
      // 10px is a deliberate token-system-compliance choice, not a visual
      // fidelity bug — flagged to ux-designer as a prototype/token
      // discrepancy in the final report rather than silently reproducing a
      // sub-floor font size.
      style={{
        fontSize: 'var(--type-caption-size)',
        fontWeight: 600,
        letterSpacing: 'var(--type-caption-tracking)',
        textTransform: 'uppercase',
        color: 'var(--hue-primary)',
      }}
    >
      {children}
    </div>
  );
}

function Verdict({ children }: { readonly children: ReactNode }) {
  return (
    <p style={{ fontSize: 'var(--type-body-size)', lineHeight: 1.5, color: 'var(--text-primary)', marginTop: 'var(--space-3)', marginBottom: 0, fontWeight: 500 }}>
      {children}
    </p>
  );
}

function Em({ children }: { readonly children: ReactNode }) {
  return <b style={{ color: 'var(--hue-primary)', fontWeight: 600 }}>{children}</b>;
}

/** Whole days from b to a (a - b): positive when a is later than b. */
function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(a) - Date.parse(b)) / 86_400_000);
}

function ModuleList({
  objective,
  onToggleAction,
}: {
  readonly objective: ObjectiveView;
  readonly onToggleAction: (action: ActionView) => Promise<void>;
}) {
  const nowActionId = useMemo(
    () => objective.actions.find((a) => !a.completed)?.actionId ?? null,
    [objective.actions],
  );

  return (
    <div style={{ padding: 'var(--space-5) var(--layout-page-margin-phone) 0' }}>
      {objective.actions.map((action, i) => (
        <ModuleRow
          key={action.actionId}
          action={action}
          isNow={action.actionId === nowActionId}
          isLast={i === objective.actions.length - 1}
          onToggle={() => {
            // onToggle is fire-and-forget by contract (ADR-0004: the
            // network is never on the tap path); catch here so a rejected
            // onToggleAction() doesn't become an unhandled promise
            // rejection.
            onToggleAction(action).catch((error: unknown) => {
              console.error('onToggleAction failed', error);
            });
          }}
        />
      ))}
    </div>
  );
}

function ModuleRow({
  action,
  isNow,
  isLast,
  onToggle,
}: {
  readonly action: ActionView;
  readonly isNow: boolean;
  readonly isLast: boolean;
  readonly onToggle: () => void;
}) {
  const tag = action.completed
    ? action.completedOn
      ? formatShortDate(action.completedOn)
      : ''
    : isNow
      ? 'now'
      : action.dueDate
        ? formatShortDate(action.dueDate)
        : '';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-4)',
        padding: 'var(--space-3) 0',
        borderBottom: isLast ? 'none' : '1px solid var(--border-subtle)',
      }}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={action.completed}
        aria-label={
          isNow
            ? `${action.title}, in progress`
            : action.completed
              ? `${action.title}, completed`
              : action.title
        }
        onClick={onToggle}
        style={{
          // Touch-target hit-slop (touch.targetMin, 44pt), matching
          // CheckControl's pattern: the button itself is sized to the full
          // touch target, negative-margined so it doesn't disturb the row's
          // visual layout, and the visible 20px dot is an inner span.
          width: 'var(--touch-target-min)',
          height: 'var(--touch-target-min)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
          marginTop: '-12px',
          marginBottom: '-12px',
          marginLeft: '-12px',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 'var(--icon-md)',
            height: 'var(--icon-md)',
            borderRadius: 'var(--radius-pill)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: action.completed
              ? 'none'
              : isNow
                ? '2px solid var(--hue-primary-vivid)'
                : '2px solid var(--border-strong)',
            background: action.completed ? 'var(--hue-primary-vivid)' : 'transparent',
          }}
        >
          {/* Bug-fix (objective-detail.md SS1.4): checkmark icon always
              rendered in markup, revealed only by completion state — never
              conditionally mounted per-row, so toggling ANY row to done shows
              its checkmark, not just a pre-seeded one. */}
          <span style={{ display: action.completed ? 'block' : 'none' }}>
            <Check size={11} strokeWidth={3.4} color="var(--text-on-accent)" aria-hidden="true" />
          </span>
        </span>
      </button>
      <span
        style={{
          fontSize: 'var(--type-body-size)',
          lineHeight: 'var(--type-body-line)',
          flex: 1,
          color: action.completed ? 'var(--text-muted)' : isNow ? 'var(--text-primary)' : 'var(--text-secondary)',
          fontWeight: isNow ? 600 : 500,
        }}
      >
        {action.title}
      </span>
      <span
        className="num"
        style={{
          fontSize: 'var(--type-caption-size)',
          color: isNow ? 'var(--hue-primary)' : 'var(--text-muted)',
          fontWeight: isNow ? 600 : 400,
        }}
      >
        {tag}
      </span>
    </div>
  );
}
